import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type RefObject,
} from 'react';

export const INVITE_CODE_DIALOG_ID = 'invite-code-dialog';

const INVITE_CODE_DESCRIPTION_ID = 'invite-code-description';
const INVITE_CODE_FEEDBACK_ID = 'invite-code-feedback';

type Feedback = {
  kind: 'error' | 'status';
  message: string;
};

type InviteCodeDialogProps = {
  open: boolean;
  onClose: () => void;
  returnFocusRef: RefObject<HTMLButtonElement | null>;
};

function focusableElements(container: HTMLElement) {
  return Array.from(
    container.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((element) => !element.hasAttribute('hidden'));
}

export function InviteCodeDialog({
  open,
  onClose,
  returnFocusRef,
}: InviteCodeDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [code, setCode] = useState('');
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  const closeDialog = useCallback(() => {
    setFeedback(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    const returnFocusTo = returnFocusRef.current;
    document.body.style.overflow = 'hidden';
    inputRef.current?.focus({ preventScroll: true });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeDialog();
        return;
      }

      if (event.key !== 'Tab' || !dialogRef.current) return;

      const elements = focusableElements(dialogRef.current);
      const first = elements[0];
      const last = elements.at(-1);

      if (!dialogRef.current.contains(document.activeElement)) {
        event.preventDefault();
        inputRef.current?.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      returnFocusTo?.focus({ preventScroll: true });
    };
  }, [closeDialog, open, returnFocusRef]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (code.trim().length === 0) {
      setFeedback({ kind: 'error', message: 'Enter your invite code to continue.' });
      inputRef.current?.focus();
      return;
    }

    setFeedback({
      kind: 'status',
      message: 'Invite-code validation is not active yet. Your code has not been submitted.',
    });
  };

  if (!open) return null;

  const feedbackId = feedback ? INVITE_CODE_FEEDBACK_ID : undefined;
  const describedBy = [INVITE_CODE_DESCRIPTION_ID, feedbackId].filter(Boolean).join(' ');

  return (
    <div
      className="invite-dialog-backdrop"
      data-testid="invite-dialog-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) closeDialog();
      }}
    >
      <div
        className="invite-dialog"
        id={INVITE_CODE_DIALOG_ID}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="invite-code-title"
        aria-describedby={INVITE_CODE_DESCRIPTION_ID}
      >
        <button
          className="invite-dialog-close"
          type="button"
          aria-label="Close invite-code dialog"
          onClick={closeDialog}
        >
          <span aria-hidden="true">×</span>
        </button>

        <div className="invite-dialog-copy">
          <p className="eyebrow">PRIVATE ACCESS</p>
          <h2 id="invite-code-title">Enter your invite code</h2>
          <p id={INVITE_CODE_DESCRIPTION_ID}>
            Sticket is in private testing. Enter the code you received to continue.
          </p>
        </div>

        <form className="invite-code-form" noValidate onSubmit={submit}>
          <label htmlFor="invite-code-input">Invite code</label>
          <input
            id="invite-code-input"
            ref={inputRef}
            name="invite-code"
            type="text"
            value={code}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            aria-required="true"
            aria-invalid={feedback?.kind === 'error' ? 'true' : undefined}
            aria-describedby={describedBy}
            onChange={(event) => {
              setCode(event.currentTarget.value);
              setFeedback(null);
            }}
          />

          {feedback ? (
            <p
              className={`invite-code-feedback is-${feedback.kind}`}
              id={INVITE_CODE_FEEDBACK_ID}
              role={feedback.kind === 'error' ? 'alert' : 'status'}
            >
              {feedback.message}
            </p>
          ) : null}

          <div className="invite-dialog-actions">
            <button className="invite-dialog-continue" type="submit">Continue</button>
            <button className="invite-dialog-cancel" type="button" onClick={closeDialog}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
